
import Vue from 'vue'
import Component from "vue-class-component";
import {Prop} from "vue-property-decorator";
import {namespace} from "vuex-class";
import {IOnOffHandlers} from "bonomo/dist";
import {settings} from "@/plugins/settings";
import {PermissionHelper} from "@/helpers/PermissionHelper";
import {EUserPermissions} from "@/model/EUserPermissions";
import {IAuthUserData} from "@/store/modules/authModule";

const authModule = namespace("authModule");
const lockerModule = namespace("lockerModule");

@Component({
  name: 'StandaloneScreenshotPreview',
})
export default class StandaloneScreenshotPreview extends Vue {
  @Prop()
  protected lockerId!: number;
  @Prop()
  protected standaloneId!: number;
  @Prop()
  protected deviceId!: string;

  private screenShotUrl: string = '';
  private subscribes: IOnOffHandlers = {};
  private takeScreenShotLoading: boolean = false;

  private pollingTimer: any = null;
  private pollingIntervalMs: number = 30000;
  private pollingDurationMs: number = 60000;
  private pollingStartTime: number = 0;
  public isPollingActive: boolean = false;
  private countdownTimer: any = null;
  private uiRefreshIntervalMs: number = 1000;
  private timeRemaining: number = 0;

  @authModule.Getter('userData')
  protected authUser!: IAuthUserData;

  protected get canLockerFactory() {
    const authUser = this.authUser;
    return PermissionHelper.hasPermission(authUser, EUserPermissions.PERMISSION_LOCKER_FACTORY, 1);
  }

  protected get canTakeScreenshot(): boolean {
    return !this.canLockerFactory;
  }

  protected created(): void {
    this.subscribes = this.$ons({
      'backend.call.OK.takeScreenShot': this.refreshScreenShotUrl.bind(this),
      'backend.call.KO.takeScreenShot': this.refreshScreenShotUrl.bind(this),
    });

    this.refreshScreenShotUrl();
  }

  public onTakeScreenShot() {
    this.takeScreenShotLoading = true;
    this.stopPolling();

    this.$store.dispatch('standaloneModule/screenshot', {
      lockerId: this.lockerId,
      standaloneId: this.standaloneId
    }).then(() => {
      this.startPolling();
      this.$uiBus.emit('info', this.$t('standalone.screenshot.requestSent'));
    }).catch(() => {
      this.takeScreenShotLoading = false;
      this.$uiBus.emit('alert', this.$t('standalone.screenshot.requestFailed'));
    });
  }

  public refreshScreenShotUrl() {
    const randomTimestamp = Math.floor((Math.random() * 89999) + 10000);

    const apiPath = settings.endpoint + 'standalones/' + this.deviceId + '/screenshot.jpg';
    this.screenShotUrl = apiPath + '?r=' + randomTimestamp;
  }

  public startPolling() {
    this.stopPolling();
    this.pollingStartTime = Date.now();
    this.isPollingActive = true;
    this.takeScreenShotLoading = true;
    this.timeRemaining = this.pollingDurationMs / 1000;

    this.countdownTimer = setInterval(this.updateCountdown.bind(this), this.uiRefreshIntervalMs);

    this.refreshScreenShotUrl();
    this.pollingTimer = setInterval(this.pollingCallback.bind(this), this.pollingIntervalMs);
  }

  private pollingCallback() {
    const elapsed = Date.now() - this.pollingStartTime;

    if (elapsed >= this.pollingDurationMs) {
      this.refreshScreenShotUrl();

      this.stopPolling();

      this.$uiBus.emit('success', this.$t('standalone.screenshot.updated'));
      return;
    }

    this.refreshScreenShotUrl();
  }

  public stopPolling() {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer);
      this.pollingTimer = null;
    }
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
    this.takeScreenShotLoading = false;
    this.isPollingActive = false;
    this.timeRemaining = 0;
  }

  public beforeDestroy(): void {
    this.$offs(this.subscribes);
    this.stopPolling();
  }

  private updateCountdown() {
    if (this.timeRemaining > 0) {
      this.timeRemaining -= 1;
    }
  }
}
